import { useMemo, useState } from 'react';
import { ArrowLeft, Download, Loader2, ShieldAlert } from 'lucide-react';
import { Link } from 'react-router-dom';
import {
    useClientPhiAccessReport,
    useExportClientPhiAccess,
    type ClientPhiAccessFilter,
} from '@/api/reports';
import { useClientNames } from '@/api/clients';
import { useUserDirectory } from '@/api/users';
import { useAuthStore } from '@/stores/auth';
import { Button } from '@/components/ui/Button';
import { Card, CardBody, CardHeader, CardTitle, CardSubtitle } from '@/components/ui/Card';
import { Input } from '@/components/ui/FormField';

import { extractErrorMessage } from '@/lib/extractErrorMessage';
import { toIsoDate } from '@/lib/calendar';
/**
 * Client PHI Access — narrow-focus disclosure list for one client.
 *
 * Use case: HIPAA §164.528 "accounting of disclosures" — a guardian asks
 * "who at your practice has accessed my chart in the last 6 months?" The
 * page produces a date-windowed event list plus per-user, per-category, and
 * per-month rollups so the privacy officer can answer without grepping the
 * raw audit log.
 *
 * Permission: gated on the existing `view_audit_log` permission — same data
 * the audit log surface already exposes, just re-projected for one client.
 */
export function ClientPhiAccessPage() {
    const canExport = useAuthStore((s) => s.hasPermission('export_reports'));

    const [draft,  setDraft]  = useState<Partial<ClientPhiAccessFilter>>({});
    const [filter, setFilter] = useState<ClientPhiAccessFilter | null>(null);

    const { data, isLoading, isError, error, isFetching } = useClientPhiAccessReport(filter);
    const exportM = useExportClientPhiAccess();

    const clientsQuery = useClientNames();
    const staffQuery   = useUserDirectory();

    const clientOptions = useMemo(() => {
        if (!clientsQuery.data) return [];
        return [...clientsQuery.data]
            .map((c) => ({ id: c.id, label: `${c.last}, ${c.first}` }))
            .sort((a, b) => a.label.localeCompare(b.label));
    }, [clientsQuery.data]);

    const userOptions = useMemo(() => {
        if (!staffQuery.data) return [];
        return staffQuery.data
            .map((s) => ({ id: s.id, label: `${s.last}, ${s.first}` }))
            .sort((a, b) => a.label.localeCompare(b.label));
    }, [staffQuery.data]);

    function apply() {
        if (!draft.clientId) return;
        setFilter({
            clientId: draft.clientId,
            userId:   draft.userId,
            dateFrom: draft.dateFrom,
            dateTo:   draft.dateTo,
        });
    }
    function clear() { setDraft({}); setFilter(null); }

    async function runExport() {
        if (!filter) return;
        try { await exportM.mutateAsync(filter); } catch { /* surfaced below */ }
    }

    const fmtTs = (iso: string) => {
        const d = new Date(iso);
        if (Number.isNaN(d.getTime())) return iso;
        return d.toLocaleString();
    };

    return (
        <div className="p-6 max-w-[1400px] mx-auto space-y-4">
            <div className="flex items-center justify-between">
                <div>
                    <Link to="/reporting" className="text-xs text-text-subtle hover:text-text inline-flex items-center gap-1 mb-1">
                        <ArrowLeft className="w-3 h-3" /> All reports
                    </Link>
                    <h1 className="font-display text-2xl font-bold text-text">Client PHI Access</h1>
                    <p className="text-xs text-text-subtle">
                        Disclosure-accounting view for a single client (HIPAA &sect;164.528). Lists every audited access to this client&apos;s chart over a date window, grouped by user, category, and month.
                    </p>
                </div>
                {canExport && (
                    <Button variant="primary" size="sm" onClick={runExport} disabled={!filter || exportM.isPending || isLoading}>
                        {exportM.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
                        Export CSV
                    </Button>
                )}
            </div>

            <Card>
                <CardBody>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
                        <label className="text-xs block">
                            <span className="text-text-muted">Client <span className="text-error">*</span></span>
                            <select
                                value={draft.clientId ?? ''}
                                onChange={(e) => setDraft({ ...draft, clientId: e.target.value || undefined })}
                                className="mt-1 h-8 w-full px-2 text-xs bg-background border border-border rounded-md text-text focus:border-accent focus:outline-none"
                            >
                                <option value="">Select a client…</option>
                                {clientOptions.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
                            </select>
                        </label>
                        <label className="text-xs block">
                            <span className="text-text-muted">User</span>
                            <select
                                value={draft.userId ?? ''}
                                onChange={(e) => setDraft({ ...draft, userId: e.target.value || undefined })}
                                className="mt-1 h-8 w-full px-2 text-xs bg-background border border-border rounded-md text-text focus:border-accent focus:outline-none"
                            >
                                <option value="">All users</option>
                                {userOptions.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
                            </select>
                        </label>
                        <label className="text-xs block">
                            <span className="text-text-muted">From</span>
                            <Input
                                type="date"
                                min="1900-01-01"
                                max={toIsoDate(new Date())}
                                value={draft.dateFrom ?? ''}
                                onChange={(e) => setDraft({ ...draft, dateFrom: e.target.value || undefined })}
                                className="mt-1 h-8"
                            />
                        </label>
                        <label className="text-xs block">
                            <span className="text-text-muted">To</span>
                            <Input
                                type="date"
                                min={draft.dateFrom || '1900-01-01'}
                                max={toIsoDate(new Date())}
                                value={draft.dateTo ?? ''}
                                onChange={(e) => setDraft({ ...draft, dateTo: e.target.value || undefined })}
                                className="mt-1 h-8"
                            />
                        </label>
                    </div>
                    <div className="flex items-center gap-2 mt-3">
                        <Button variant="primary" size="sm" onClick={apply} disabled={!draft.clientId}>Run report</Button>
                        <Button variant="ghost" size="sm" onClick={clear}>Clear</Button>
                        {isFetching && <Loader2 className="w-3.5 h-3.5 animate-spin text-text-subtle" />}
                    </div>
                </CardBody>
            </Card>

            {!filter ? (
                <Card>
                    <CardBody>
                        <p className="text-sm text-text-muted">Pick a client and run the report to see disclosure-grade access events.</p>
                    </CardBody>
                </Card>
            ) : isError ? (
                <Card>
                    <CardBody>
                        <p className="text-sm text-error font-medium">Couldn&apos;t load report</p>
                        <p className="text-xs text-text-subtle mt-1">{extractErrorMessage(error, 'Please try again.')}</p>
                    </CardBody>
                </Card>
            ) : isLoading || !data ? (
                <Card><CardBody><div className="flex items-center gap-2 text-text-subtle text-sm"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div></CardBody></Card>
            ) : (
                <>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        <Kpi label="Events" value={data.kpis.totalEvents.toLocaleString()} />
                        <Kpi label="Distinct users" value={data.kpis.distinctUsers.toLocaleString()} />
                        <Kpi label="First event" value={data.kpis.firstEventAt ? new Date(data.kpis.firstEventAt).toLocaleDateString() : '—'} />
                        <Kpi label="Last event"  value={data.kpis.lastEventAt  ? new Date(data.kpis.lastEventAt).toLocaleDateString()  : '—'} />
                    </div>

                    {data.coverage.nameMatchUsed && (
                        <Card className="border-warning">
                            <CardBody>
                                <div className="flex items-start gap-2 text-xs text-warning-text">
                                    <ShieldAlert className="w-4 h-4 mt-0.5 shrink-0" />
                                    <span>
                                        Disclosure note: results include audit rows whose subject matched <strong>{data.client.name}</strong> by display name as well as by UUID. If two clients in this practice share the same name, spot-check the events list before signing the disclosure response.
                                    </span>
                                </div>
                            </CardBody>
                        </Card>
                    )}

                    {data.truncated && (
                        <Card className="border-warning">
                            <CardBody>
                                <p className="text-xs text-warning-text">
                                    Result set was truncated at 5,000 events. Narrow the date window to see the rest.
                                </p>
                            </CardBody>
                        </Card>
                    )}

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                        <RollupCard title="By user" subtitle="Who looked at this chart, and how often.">
                            {data.byUser.length === 0
                                ? <Empty>No matching events.</Empty>
                                : <CountTable rows={data.byUser.map((u) => ({ label: u.userName, count: u.count }))} firstColLabel="User" />}
                        </RollupCard>
                        <RollupCard title="By month" subtitle="Cadence of access across the date window.">
                            {data.byMonth.length === 0
                                ? <Empty>No matching events.</Empty>
                                : <CountTable rows={data.byMonth.map((m) => ({ label: m.month, count: m.count }))} firstColLabel="Month" />}
                        </RollupCard>
                        <RollupCard title="By category" subtitle="Clinical / Billing / Audit / Auth split.">
                            {data.byCategory.length === 0
                                ? <Empty>No matching events.</Empty>
                                : <CountTable rows={data.byCategory.map((c) => ({ label: c.category, count: c.count }))} firstColLabel="Category" />}
                        </RollupCard>
                        <RollupCard title="By action" subtitle="What kind of access happened.">
                            {data.byAction.length === 0
                                ? <Empty>No matching events.</Empty>
                                : <CountTable rows={data.byAction.map((a) => ({ label: a.action, count: a.count }))} firstColLabel="Action" />}
                        </RollupCard>
                    </div>

                    <Card>
                        <CardHeader>
                            <div>
                                <CardTitle>Events</CardTitle>
                                <CardSubtitle>
                                    Newest first. Use Export to save the full list for a §164.528 disclosure response.
                                </CardSubtitle>
                            </div>
                        </CardHeader>
                        <CardBody className="pt-0 overflow-x-auto">
                            {data.events.length === 0 ? (
                                <Empty>No matching events.</Empty>
                            ) : (
                                <table className="w-full text-sm">
                                    <thead>
                                        <tr className="text-left text-2xs uppercase tracking-wider text-text-subtle border-b border-border">
                                            <th className="px-3 py-2 font-bold">When</th>
                                            <th className="px-3 py-2 font-bold">User</th>
                                            <th className="px-3 py-2 font-bold">Category</th>
                                            <th className="px-3 py-2 font-bold">Action</th>
                                            <th className="px-3 py-2 font-bold">Subject</th>
                                            <th className="px-3 py-2 font-bold">Result</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {data.events.map((e) => (
                                            <tr key={e.id} className="border-b border-border last:border-b-0">
                                                <td className="px-3 py-2 text-xs tabular-nums whitespace-nowrap">{fmtTs(e.ts)}</td>
                                                <td className="px-3 py-2 text-xs">{e.userName || '—'}</td>
                                                <td className="px-3 py-2 text-xs text-text-muted">{e.category}</td>
                                                <td className="px-3 py-2">{e.action}</td>
                                                <td className="px-3 py-2 text-xs text-text-muted break-words">{e.subject}</td>
                                                <td className="px-3 py-2 text-xs">{e.result}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </CardBody>
                    </Card>
                </>
            )}
        </div>
    );
}

function Kpi({ label, value }: { label: string; value: string }) {
    return (
        <Card>
            <CardBody>
                <p className="text-2xs uppercase tracking-wider text-text-subtle">{label}</p>
                <p className="font-display text-xl font-bold mt-1 tabular-nums text-text">{value}</p>
            </CardBody>
        </Card>
    );
}

function RollupCard({ title, subtitle, children }: { title: string; subtitle: string; children: React.ReactNode }) {
    return (
        <Card>
            <CardHeader>
                <div>
                    <CardTitle>{title}</CardTitle>
                    <CardSubtitle>{subtitle}</CardSubtitle>
                </div>
            </CardHeader>
            <CardBody className="pt-0 overflow-x-auto">{children}</CardBody>
        </Card>
    );
}

function CountTable({ rows, firstColLabel }: { rows: { label: string; count: number }[]; firstColLabel: string }) {
    return (
        <table className="w-full text-sm">
            <thead>
                <tr className="text-left text-2xs uppercase tracking-wider text-text-subtle border-b border-border">
                    <th className="px-3 py-2 font-bold">{firstColLabel}</th>
                    <th className="px-3 py-2 font-bold text-right">Events</th>
                </tr>
            </thead>
            <tbody>
                {rows.map((r) => (
                    <tr key={r.label} className="border-b border-border last:border-b-0">
                        <td className="px-3 py-2">{r.label}</td>
                        <td className="px-3 py-2 text-right tabular-nums">{r.count}</td>
                    </tr>
                ))}
            </tbody>
        </table>
    );
}

function Empty({ children }: { children: React.ReactNode }) {
    return <p className="text-xs text-text-subtle px-3 py-4">{children}</p>;
}
